import React, { useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { styled } from '@mui/material/styles'
import { EmtButton, EmtChip, EmtIcon, EmtTheme } from 'emt-components-v2'
import NextImage from 'next/image'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Blur from '@/assets/img/blur.png'
import { Constants } from '@/assets'
import { fixImagePath } from '@/modules/Image'
import { getProfileActivityBadge } from '@/helpers/profileActivityBadge'

type Props = React.PropsWithChildren<{
  picturesUrls: string[]
  onClick: (imageIndex: number) => void
  userIsNew?: boolean
  userLastRefresh?: string
  editButtonOnClick?: () => void
  publicProfile?: boolean
  color?: string
  reviewsCount?: number
  rating?: number
  distance?: number
  averageResponseTimeUnderThreeHours?: boolean
}>

type EmtThemeType = { theme: EmtTheme }

const StyledImageList = styled(Box)<EmtThemeType>(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  gap: '20px',
  touchAction: 'manipulation',

  '& img': {
    cursor: 'pointer',
    userSelect: 'none',
    WebkitUserDrag: 'none',
    touchAction: 'manipulation',
  },

  '& .image, & .first-image': {
    touchAction: 'manipulation',
  },

  '& .first-image': {
    position: 'relative',
    height: '390px',
    width: '595px',
    borderTopLeftRadius: '16px',
    borderBottomLeftRadius: '16px',
    overflow: 'hidden',
    '& img': {
      objectFit: 'cover',
      borderTopLeftRadius: '16px',
      borderBottomLeftRadius: '16px',
    },
  },
  '& .side-images': {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '20px',
    '& *': {
      position: 'relative',
      width: '185px',
      height: '185px',
      '& img': {
        objectFit: 'cover',
      },
    },
  },
  '& .empty-image': {
    background: theme.palette.greyGradient[100],
  },
  [theme.breakpoints.down('lg')]: {
    '& .first-image': {
      height: '340px',
      width: '520px',
    },
    '& .side-images': {
      rowGap: '20px',
      flex: 1,
      '& *': {
        width: '160px',
        height: '160px',
      },
    },
  },
  [theme.breakpoints.down(1080)]: {
    '& .first-image': {
      height: '287px',
      width: '440px',
    },
    '& .side-images': {
      rowGap: '20px',
      flex: 1,
      '& *': {
        width: '133px',
        height: '133px',
      },
    },
  },
})) as typeof Box

const UserImagesList: React.FC<Props> = ({
  picturesUrls: [profileImageUrl, ...imagesUrls],
  onClick,
  userIsNew = false,
  userLastRefresh,
  editButtonOnClick,
  publicProfile = false,
  color = 'primary',
  reviewsCount,
  rating,
  distance,
  averageResponseTimeUnderThreeHours,
}) => {
  const { t } = useTranslation('profile')
  const { t: tc } = useTranslation('common')

  const imagesUrl = [profileImageUrl || null, ...imagesUrls]
  const [handledErrors, setHandledErrors] = useState(new Array(imagesUrl.length).fill(false))

  const activityBadge = useMemo(
    () =>
      getProfileActivityBadge({
        isNew: userIsNew,
        lastRefresh: userLastRefresh,
        reviewsCount,
        rating,
        distance,
        averageResponseTimeUnderThreeHours,
      }),
    [userIsNew, userLastRefresh, reviewsCount, rating, distance, averageResponseTimeUnderThreeHours],
  )

  const activityBadgeLabel = useMemo(
    () =>
      activityBadge
        ? tc(activityBadge.key, {
            defaultValue: activityBadge.defaultValue,
          })
        : undefined,
    [activityBadge, tc],
  )

  const handleImageError = (e: any, index: number) => {
    if (handledErrors[index]) return
    fixImagePath(Constants.apiUrl, e.target)
    const newArray = [...handledErrors]
    newArray[index] = true
    setHandledErrors(newArray)
  }

  return (
    <StyledImageList>
      {imagesUrl[0] ? (
        <Box className="first-image">
          <NextImage
            priority
            placeholder="blur"
            blurDataURL={Blur.blurDataURL}
            layout="fill"
            src={imagesUrl[0]}
            quality={70}
            sizes="(max-width: 900px) 100vw, 595px"
            onClick={() => onClick(0)}
            alt={''}
          />
        </Box>
      ) : (
        <Box className="first-image empty-image" />
      )}

      <Box className="side-images">
        {imagesUrl[1] ? (
          <Box className="image" sx={{ borderTopRightRadius: '16px', overflow: 'hidden' }}>
            <NextImage
              placeholder="blur"
              blurDataURL={Blur.blurDataURL}
              layout="fill"
              src={imagesUrl[1] || ''}
              quality={70}
              sizes="(max-width: 900px) 33vw, 185px"
              onClick={() => onClick(1)}
              alt={''}
              onError={(e) => {
                handleImageError(e, 1)
              }}
            />
          </Box>
        ) : (
          <Box
            className="empty-image"
            sx={{
              borderTopRightRadius: '16px',
            }}
          />
        )}
        {imagesUrl[2] ? (
          <Box className={`image relative`} sx={{ borderBottomRightRadius: '16px', overflow: 'hidden' }}>
            <NextImage
              placeholder="blur"
              blurDataURL={Blur.blurDataURL}
              layout="fill"
              src={imagesUrl[2] || ''}
              quality={70}
              sizes="(max-width: 900px) 33vw, 185px"
              onClick={() => onClick(2)}
              alt={''}
              onError={(e) => {
                handleImageError(e, 2)
              }}
            />
          </Box>
        ) : (
          <Box className="empty-image" sx={{ borderBottomRightRadius: '16px' }} />
        )}
      </Box>

      {imagesUrl.length && !publicProfile ? (
        <Box sx={{ position: 'absolute', bottom: editButtonOnClick ? 70 : 20, left: 20 }}>
          <EmtButton color={color} variant="whiteOutlined" sx={{ border: 0 }} onClick={() => onClick(0)}>
            {t('images.showAll', { count: imagesUrl.length })}
          </EmtButton>
        </Box>
      ) : null}

      {editButtonOnClick ? (
        <Box sx={{ position: 'absolute', bottom: 20, left: 20 }}>
          <EmtButton
            color={color}
            sx={{ border: 0 }}
            onClick={editButtonOnClick}
            startIcon={<EmtIcon className="emt-edit" />}
          >
            {t('images.editButton')}
          </EmtButton>
        </Box>
      ) : null}

      {activityBadgeLabel ? (
        <EmtChip
          label={activityBadgeLabel}
          color="transparent"
          sx={{ position: 'absolute', top: '20px', left: '20px', fontWeight: '500' }}
        />
      ) : null}
    </StyledImageList>
  )
}

export const UserImagesListLoading: React.FC = () => (
  <StyledImageList>
    <Skeleton variant="rectangular" className="first-image" />

    <Box className="side-images">
      <Skeleton
        variant="rectangular"
        sx={{
          borderTopRightRadius: '16px',
        }}
      />

      <Skeleton
        variant="rectangular"
        sx={{
          borderBottomRightRadius: '16px',
        }}
      />
    </Box>
  </StyledImageList>
)

export default UserImagesList
